// MembersPicker — general People + Groups selection control. Collapsed trigger
// (summary + first-selected avatar + chevron) expanding to a panel with a
// People/Groups pill toggle, a search box, and a checkbox list of avatar rows.
// Controlled selection (id arrays in/out); options are self-fetched from the
// users/groups selection endpoints once the panel first opens.

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Avatar from './Avatar';
import AvatarStack from './AvatarStack';
import Dropdown from './Dropdown';
import Icon from './Icon';
import Input from './Input';
import SegmentedControl from './SegmentedControl';
import { useGetUsersSelectionQuery } from '@/api/endpoints/usersApi';
import { useGetGroupsSelectionQuery } from '@/api/endpoints/groupsApi';
import initialsOf from '@/utils/initials';

export interface MembersPickerProps {
  selectedUserIds: string[];
  selectedGroupIds: string[];
  onChangeUsers: (ids: string[]) => void;
  onChangeGroups: (ids: string[]) => void;
  /** Users that always render checked and can't be toggled (e.g. the project
   *  creator / company owner). Purely presentational — never added to
   *  `selectedUserIds` and never emitted through `onChangeUsers`. */
  lockedUserIds?: string[];
  disabled?: boolean;
}

const toggleId = (ids: string[], id: string) =>
  ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id];

const MembersPicker = ({
  selectedUserIds,
  selectedGroupIds,
  onChangeUsers,
  onChangeGroups,
  lockedUserIds = [],
  disabled = false,
}: MembersPickerProps) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [opened, setOpened] = useState(false); // fetch only after first open
  const [tab, setTab] = useState<'people' | 'groups'>('people');
  const [search, setSearch] = useState('');

  const usersQ = useGetUsersSelectionQuery(undefined, { skip: !opened });
  const groupsQ = useGetGroupsSelectionQuery(undefined, { skip: !opened });
  const users = usersQ.data ?? [];
  const groups = groupsQ.data ?? [];

  const q = search.trim().toLowerCase();
  const shownUsers = q ? users.filter((u) => u.name.toLowerCase().includes(q)) : users;
  const shownGroups = q ? groups.filter((g) => g.name.toLowerCase().includes(q)) : groups;
  const loading = tab === 'people' ? usersQ.isLoading : groupsQ.isLoading;

  const selectedUsers = useMemo(
    () => users.filter((u) => selectedUserIds.includes(u.id)),
    [users, selectedUserIds],
  );

  const summary = () => {
    const people = selectedUserIds.length;
    const groupCount = selectedGroupIds.length;
    if (people === 0 && groupCount === 0) return t('members_placeholder');
    const parts: string[] = [];
    if (people > 0)
      parts.push(people === 1 ? t('members_person') : t('members_people', { count: people }));
    if (groupCount > 0) parts.push(t('members_groups_suffix', { count: groupCount }));
    return parts.join(' · ');
  };

  const openPanel = () => {
    setOpened(true);
    setOpen((o) => !o);
    setSearch('');
  };

  return (
    <Dropdown
      open={open}
      onClose={() => setOpen(false)}
      align='start'
      className='members-picker'
      trigger={
        <button
          type='button'
          className={`members-picker__trigger${open ? ' is-open' : ''}`}
          aria-expanded={open}
          disabled={disabled}
          onClick={openPanel}
        >
          <Icon name='users' size={18} className='members-picker__trigger-icon' />
          <span
            className={`members-picker__summary${
              selectedUserIds.length + selectedGroupIds.length === 0 ? ' is-placeholder' : ''
            }`}
          >
            {summary()}
          </span>
          <span className='members-picker__trigger-end'>
            {selectedUsers.length > 0 && (
              <AvatarStack max={3} size={28}>
                {selectedUsers.map((user) => (
                  <Avatar key={user.id} initials={initialsOf(user.name)} size={28} />
                ))}
              </AvatarStack>
            )}
            <Icon
              name={open ? 'chevron-up' : 'chevron-down'}
              size={16}
              className='members-picker__caret'
            />
          </span>
        </button>
      }
    >
      <div className='members-picker__panel'>
        <SegmentedControl
          aria-label={t('members_placeholder')}
          value={tab}
          onChange={(v) => {
            setTab(v as 'people' | 'groups');
            setSearch('');
          }}
          options={[
            {
              value: 'people',
              label: t('members_tab_people'),
              icon: 'user',
              count: selectedUserIds.length,
            },
            {
              value: 'groups',
              label: t('members_tab_groups'),
              icon: 'users',
              count: selectedGroupIds.length,
            },
          ]}
        />

        <Input
          size='sm'
          iconStart='search'
          placeholder={tab === 'people' ? t('members_search_people') : t('members_search_groups')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        <div className='members-picker__list'>
          {loading ? (
            <span className='members-picker__empty'>{t('text_loading')}</span>
          ) : tab === 'people' ? (
            shownUsers.length === 0 ? (
              <span className='members-picker__empty'>{t('members_no_results')}</span>
            ) : (
              shownUsers.map((user) => {
                const locked = lockedUserIds.includes(user.id);
                const checked = locked || selectedUserIds.includes(user.id);
                return (
                  <label
                    key={user.id}
                    className={`members-picker__row${checked ? ' is-selected' : ''}${
                      locked ? ' is-locked' : ''
                    }`}
                  >
                    <input
                      type='checkbox'
                      className='app-checkbox__input'
                      checked={checked}
                      disabled={locked}
                      onChange={() => !locked && onChangeUsers(toggleId(selectedUserIds, user.id))}
                    />
                    <span className='app-checkbox__box' aria-hidden='true'>
                      <Icon name='check' size={14} className='app-checkbox__check' />
                    </span>
                    <Avatar initials={initialsOf(user.name)} size={32} />
                    <span className='members-picker__row-label'>
                      <span className='members-picker__row-name'>{user.name}</span>
                      {user.email && <span className='members-picker__row-sub'>{user.email}</span>}
                    </span>
                  </label>
                );
              })
            )
          ) : shownGroups.length === 0 ? (
            <span className='members-picker__empty'>{t('members_no_results')}</span>
          ) : (
            shownGroups.map((group) => {
              const checked = selectedGroupIds.includes(group.id);
              return (
                <label
                  key={group.id}
                  className={`members-picker__row${checked ? ' is-selected' : ''}`}
                >
                  <input
                    type='checkbox'
                    className='app-checkbox__input'
                    checked={checked}
                    onChange={() => onChangeGroups(toggleId(selectedGroupIds, group.id))}
                  />
                  <span className='app-checkbox__box' aria-hidden='true'>
                    <Icon name='check' size={14} className='app-checkbox__check' />
                  </span>
                  <span className='members-picker__group-tile'>
                    <Icon name='users' size={16} />
                  </span>
                  <span className='members-picker__row-label'>
                    <span className='members-picker__row-name'>{group.name}</span>
                    <span className='members-picker__row-sub'>
                      {t('members_group_sub', { count: group.membersCount })}
                    </span>
                  </span>
                </label>
              );
            })
          )}
        </div>
      </div>
    </Dropdown>
  );
};

export default MembersPicker;
